import { all, call, cancelled, put, takeLatest } from "redux-saga/effects";
import {
  CANCEL_MARKETPLACE_DASHBOARD_STATS,
  GET_AGG_MARKETPLACE_STATS_FOR_CAMPAIGNS,
  GET_MARKETPLACE_SOURCE_STATS,
  GET_MARKETPLACE_AD_GROUP_STATS,
  GET_MARKETPLACE_DASHBOARD_STATS
} from "./actionTypes";
import {
  getAggMarketplaceStatsForCampaignsSuccess,
  getAggMarketplaceStatsForCampaignsError,
  getMarketplaceSourceStatsSuccess,
  getMarketplaceSourceStatsError,
  getMarketplaceAdGroupStatsSuccess,
  getMarketplaceAdGroupStatsError,
  getMarketplaceDashboardStatsSuccess,
  getMarketplaceDashboardStatsError
} from "./actions";
import { AppConfiguration } from "config"
import { genericGetRequest, genericGetRequest_2 } from "helpers/sf_backend_helper";
import { getCurrentBrandFromLocalStorage } from "helpers/sf_utils";
import { bsh_getClientUserDetails } from "helpers/sf_browser_storage_helper";

// V4 API saga for Goal Marketplace (leads) campaign aggregated data
export function* onGetAggMarketplaceStatsForCampaigns(action) {
  try {
    let user_auth = bsh_getClientUserDetails()
    const currentBrand = getCurrentBrandFromLocalStorage()

    const response = yield call(
      genericGetRequest_2,
      `${AppConfiguration.BACKEND_URL}/api/v4/campaign_stats/leads`,
      {
        start_date: action.payload.start_date,
        end_date: action.payload.end_date
      },
      {
        Authorization: user_auth.token,
        account_id: user_auth.account_id,
        brand_id: currentBrand?.id,
        payload: action.payload
      }
    )

    yield put(getAggMarketplaceStatsForCampaignsSuccess(response.data))
  } catch (error) {
    yield put(getAggMarketplaceStatsForCampaignsError(error))
  }
}

// V4 API saga for Goal Marketplace (leads) source stats of a single campaign
export function* onGetMarketplaceSourceStats(action) {
  try {
    let user_auth = bsh_getClientUserDetails()
    const { campaign_id, ...queryParams } = action.payload || {}

    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v4/campaigns/${campaign_id}/source_stats/leads`,
      queryParams,
      {
        Authorization: user_auth.token,
        "account-id": user_auth.account_id,
        "brand-id": getCurrentBrandFromLocalStorage()?.id
      }
    )

    yield put(getMarketplaceSourceStatsSuccess(response))
  } catch (error) {
    yield put(getMarketplaceSourceStatsError(error))
  }
}

// V4 API saga for Goal Marketplace (leads) ad group stats of a single campaign
export function* onGetMarketplaceAdGroupStats(action) {
  try {
    let user_auth = bsh_getClientUserDetails()
    const { campaign_id, ...queryParams } = action.payload || {}

    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v4/campaigns/${campaign_id}/ad_group_stats/leads`,
      queryParams,
      {
        Authorization: user_auth.token,
        "account-id": user_auth.account_id,
        "brand-id": getCurrentBrandFromLocalStorage()?.id
      }
    )

    yield put(getMarketplaceAdGroupStatsSuccess(response))
  } catch (error) {
    yield put(getMarketplaceAdGroupStatsError(error))
  }
}

// One request per dashboard payload, matching the backend v4 actions.
// genericGetRequest_2 drops null params instead of serialising them as the
// string "null", which the backend's integer-array parser rejects.
function* fetchMarketplaceDashboardPayload({ payload_action, params, headers, required, signal }) {
  try {
    return yield call(
      genericGetRequest_2,
      `${AppConfiguration.BACKEND_URL}/api/v4/dashboard_stats/${payload_action}`,
      params,
      headers,
      { signal }
    )
  } catch (error) {
    if (required) throw error

    return null
  }
}

// V4 API saga for the complete Goal Marketplace (leads) dashboard. Requests run
// in parallel and settle into the existing dashboard state bucket together.
export function* onGetMarketplaceDashboardStats(action) {
  const abortController = new AbortController()

  try {
    let user_auth = bsh_getClientUserDetails()
    const headers = {
      Authorization: user_auth.token,
      "account-id": user_auth.account_id,
      "brand-id": getCurrentBrandFromLocalStorage()?.id
    }

    const {
      compare_to_start_date, compare_to_end_date, ...window_params
    } = action.payload || {}

    const main_call = call(fetchMarketplaceDashboardPayload, {
      payload_action: "leads_main_data",
      params: window_params,
      headers,
      signal: abortController.signal,
      required: true
    })

    const compare_call = (compare_to_start_date && compare_to_end_date)
      ? call(fetchMarketplaceDashboardPayload, {
        payload_action: "leads_main_data",
        params: {
          ...window_params,
          start_date: compare_to_start_date,
          end_date: compare_to_end_date
        },
        headers,
        signal: abortController.signal
      })
      : null

    // date_ranges keeps compare-to params so the graph can render its `prev`
    // series, while the dimension breakdowns use the current window only.
    const date_ranges_call = call(fetchMarketplaceDashboardPayload, {
      payload_action: "leads_date_ranges",
      params: action.payload || {},
      headers,
      signal: abortController.signal
    })
    const campaign_wise_call = call(fetchMarketplaceDashboardPayload, {
      payload_action: "leads_campaign_wise_metrics",
      params: window_params,
      headers,
      signal: abortController.signal
    })
    const lead_type_wise_call = call(fetchMarketplaceDashboardPayload, {
      payload_action: "leads_lead_type_wise_metrics",
      params: window_params,
      headers,
      signal: abortController.signal
    })
    const state_wise_call = call(fetchMarketplaceDashboardPayload, {
      payload_action: "leads_state_wise_metrics",
      params: window_params,
      headers,
      signal: abortController.signal
    })
    const source_wise_call = call(fetchMarketplaceDashboardPayload, {
      payload_action: "leads_source_wise_metrics",
      params: window_params,
      headers
    })

    const [
      main_response,
      compare_response,
      date_ranges_response,
      campaign_wise_response,
      lead_type_wise_response,
      state_wise_response,
      source_wise_response
    ] = yield all([
      main_call,
      compare_call,
      date_ranges_call,
      campaign_wise_call,
      lead_type_wise_call,
      state_wise_call,
      source_wise_call
    ])

    yield put(getMarketplaceDashboardStatsSuccess({
      main_data: main_response?.data?.main_data || [],
      compare_to_main_data: compare_response?.data?.main_data || [],
      date_ranges: date_ranges_response?.data?.date_ranges || [],
      campaign_wise_metrics:
        campaign_wise_response?.data?.campaign_wise_metrics || [],
      lead_type_wise_metrics:
        lead_type_wise_response?.data?.lead_type_wise_metrics || [],
      state_wise_metrics:
        state_wise_response?.data?.state_wise_metrics || [],
      source_wise_metrics:
        source_wise_response?.data?.source_wise_metrics || []
    }))
  } catch (error) {
    yield put(getMarketplaceDashboardStatsError(error))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
    }
  }
}

export function* onMarketplaceDashboardAction(action) {
  if (action.type === CANCEL_MARKETPLACE_DASHBOARD_STATS) return

  yield call(onGetMarketplaceDashboardStats, action)
}

function* MarketplaceStatsSaga() {
  yield takeLatest(GET_AGG_MARKETPLACE_STATS_FOR_CAMPAIGNS, onGetAggMarketplaceStatsForCampaigns);
  yield takeLatest(GET_MARKETPLACE_SOURCE_STATS, onGetMarketplaceSourceStats);
  yield takeLatest(GET_MARKETPLACE_AD_GROUP_STATS, onGetMarketplaceAdGroupStats);
  yield takeLatest(
    [GET_MARKETPLACE_DASHBOARD_STATS, CANCEL_MARKETPLACE_DASHBOARD_STATS],
    onMarketplaceDashboardAction
  );
}

export default MarketplaceStatsSaga;
